import { createContext, memo, ReactNode, useContext, useMemo } from 'react';
import {
  Dimensions,
  Platform,
  ScaledSize,
  useWindowDimensions,
} from 'react-native';
import {
  isMobileBrowser,
  isMobileLayout,
  useIsMobileLayout,
  useIsMobilePhone,
  useIsTabletLayout,
} from '../../components/styles/responsive';
import {
  PixelScaleFn,
  spx,
  spxForMobileHighDPI,
  spxForMobileWeb,
  spxForTablet,
  spxIdentity,
} from './utils';

export type ResponsiveContextProps = {
  scaled: PixelScaleFn;
  rpx: PixelScaleFn;
  dimens: ScaledSize;
  isMobile: boolean;
  isMobilePhone: boolean;
  isTablet: boolean;
};

export const ResponsiveContext = createContext<ResponsiveContextProps>(
  null as any,
);

const getDefaultResponsiveProps = (): ResponsiveContextProps => ({
  scaled: spxIdentity,
  rpx: spxIdentity,
  isMobile: isMobileLayout(),
  isMobilePhone: false,
  isTablet: false,
  dimens: Dimensions.get('window'),
});

export const ResponsiveContextProvider = memo(
  ({
    children,
    highDPI = true,
  }: {
    children?: ReactNode;
    highDPI?: boolean;
  }) => {
    const { width, height, scale, fontScale } = useWindowDimensions();
    const isMobilePhone = useIsMobilePhone();
    const isMobile = useIsMobileLayout();
    const isTablet = useIsTabletLayout();

    const rpx =
      Platform.OS === 'web'
        ? isMobile || isMobileBrowser()
          ? spxForMobileWeb
          : spxIdentity
        : isMobile
          ? highDPI
            ? spxForMobileHighDPI
            : spx
          : spxForTablet;

    const value = useMemo<ResponsiveContextProps>(
      () => ({
        scaled: rpx,
        rpx,
        isMobile,
        isTablet,
        isMobilePhone,
        dimens: {
          width,
          height,
          scale,
          fontScale,
        },
      }),
      [fontScale, height, isMobile, isMobilePhone, isTablet, rpx, scale, width],
    );

    return (
      <ResponsiveContext.Provider value={value}>
        {children}
      </ResponsiveContext.Provider>
    );
  },
);

export const useResponsiveContext = () => {
  return useContext(ResponsiveContext) ?? getDefaultResponsiveProps();
};
